import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass';

// Easing functions
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const easeOutBack = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

// Create gear geometry
const createGearGeometry = () => {
  const shape = new THREE.Shape();
  const outerRadius = 1;
  const innerRadius = 0.3;
  const teeth = 12;
  const toothDepth = 0.15;
  
  for (let i = 0; i < teeth; i++) {
    const angle1 = (i / teeth) * Math.PI * 2;
    const angle2 = ((i + 0.3) / teeth) * Math.PI * 2;
    const angle3 = ((i + 0.5) / teeth) * Math.PI * 2;
    const angle4 = ((i + 0.8) / teeth) * Math.PI * 2;
    const r1 = outerRadius - toothDepth;
    const r2 = outerRadius;
    if (i === 0) shape.moveTo(Math.cos(angle1) * r1, Math.sin(angle1) * r1);
    shape.lineTo(Math.cos(angle2) * r2, Math.sin(angle2) * r2);
    shape.lineTo(Math.cos(angle3) * r2, Math.sin(angle3) * r2);
    shape.lineTo(Math.cos(angle4) * r1, Math.sin(angle4) * r1);
  }
  shape.closePath();
  const holePath = new THREE.Path();
  holePath.absellipse(0, 0, innerRadius, innerRadius, 0, Math.PI * 2, true);
  shape.holes.push(holePath);
  return new THREE.ExtrudeGeometry(shape, { steps: 1, depth: 0.3, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 1 });
};

// Create shadow plane
const createShadowPlane = (size = 1.5) => {
  const geometry = new THREE.CircleGeometry(size, 32);
  const material = new THREE.MeshBasicMaterial({
    color: 0x000000, transparent: true, opacity: 0.2, side: THREE.DoubleSide, depthWrite: false
  });
  const plane = new THREE.Mesh(geometry, material);
  plane.rotation.x = -Math.PI / 2;
  plane.name = 'shadowPlane';
  plane.userData.isShadow = true;
  return plane;
};

// Create connector line
const createConnectorLine = (start, end) => {
  const curve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(start.x, start.y, start.z),
    new THREE.Vector3((start.x + end.x) / 2, Math.max(start.y, end.y) + 0.5, (start.z + end.z) / 2),
    new THREE.Vector3(end.x, end.y, end.z)
  );
  const points = curve.getPoints(20);
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineDashedMaterial({
    color: 0x00d4ff, dashSize: 0.1, gapSize: 0.05, transparent: true, opacity: 0.5
  });
  const line = new THREE.Line(geometry, material);
  line.computeLineDistances();
  line.name = 'connectorLine';
  return line;
};

// Create piston group
const createPistonGroup = (wireframe = false) => {
  const group = new THREE.Group();
  const parts = [
    { name: 'cylinder', geo: new THREE.CylinderGeometry(0.6, 0.6, 1.5, 32), color: '#3E5B78', pos: [0, 0, 0], explodeDir: [0, 0, -1.5], opacity: 0.6 },
    { name: 'piston_head', geo: new THREE.CylinderGeometry(0.5, 0.5, 0.3, 32), color: '#5AA7F7', pos: [0, 0.5, 0], explodeDir: [0, 2, 0] },
    { name: 'connecting_rod', geo: new THREE.CylinderGeometry(0.1, 0.1, 1, 16), color: '#9FB3C8', pos: [0, 0, 0], explodeDir: [0, 1, 1] },
    { name: 'crankshaft', geo: new THREE.CylinderGeometry(0.15, 0.15, 0.8, 16), color: '#0066CC', pos: [0, -1, 0], explodeDir: [0, -2, 0], rotZ: Math.PI / 2 }
  ];
  
  parts.forEach(({ name, geo, color, pos, explodeDir, opacity, rotZ }, i) => {
    const mat = new THREE.MeshStandardMaterial({
      color, wireframe, metalness: 0.7, roughness: 0.2, transparent: opacity !== undefined, opacity: opacity || 1
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(...pos);
    if (rotZ) mesh.rotation.z = rotZ;
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = {
      partName: name, originalPosition: new THREE.Vector3(...pos),
      explodeDirection: new THREE.Vector3(...explodeDir),
      selectable: true, partIndex: i, floatPhase: Math.random() * Math.PI * 2
    };
    const shadow = createShadowPlane(0.8);
    shadow.position.y = -1.5;
    mesh.add(shadow);
    group.add(mesh);
  });
  return group;
};

// Main Viewer component
export const Viewer3D = forwardRef(({
  wireframe = false, showGrid = true, modelType = 'gear',
  explodeAmount = 0, measureMode = false, onMeasurementClick,
  showConnectors = true, onModelLoaded, onAnimationToggle,
  renderQuality = 'clean' // 'clean' = professional CAD look, 'stylized' = glowing effects
}, ref) => {
  const containerRef = useRef(null);
  const initRef = useRef(false);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [hasLoadedModel, setHasLoadedModel] = useState(false);
  const measureModeRef = useRef(measureMode);
  const onMeasurementClickRef = useRef(onMeasurementClick);
  const renderQualityRef = useRef(renderQuality);
  
  const threeRef = useRef({
    renderer: null, scene: null, camera: null, controls: null,
    animationId: null, gears: [], piston: null, grid: null,
    loadedModel: null, modelMixer: null, modelParts: [],
    measurementMarkers: [], measurementLines: [], connectorLines: [],
    laserLine: null, laserDot: null,
    highlightedMesh: null, originalMaterials: new Map(),
    targetExplode: 0, currentExplode: 0, explodeAnimating: false,
    floatTime: 0, clock: new THREE.Clock(),
    composer: null, bloomPass: null, envMap: null, pmremGenerator: null,
    rimLight: null, keyLight: null,
    // Section plane state
    sectionPlane: null, sectionHelper: null, sectionEnabled: false,
    sectionAxis: 'z', sectionPosition: 0
  });
  
  // Keep refs up to date (for use in effects that shouldn't re-run)
  useEffect(() => {
    measureModeRef.current = measureMode;
    onMeasurementClickRef.current = onMeasurementClick;
    renderQualityRef.current = renderQuality;
  }, [measureMode, onMeasurementClick, renderQuality]);
  
  // Calculate arc position for exploded parts
  const getArcPosition = (index, total, amount, basePos, explodeDir) => {
    const arcRadius = amount * 2;
    const arcSpread = Math.PI * 0.8; // 144 degree arc
    const startAngle = -arcSpread / 2;
    const angleStep = total > 1 ? arcSpread / (total - 1) : 0;
    const angle = startAngle + index * angleStep;
    
    // Arc offset (semi-circle arrangement)
    const arcX = Math.sin(angle) * arcRadius;
    const arcY = Math.cos(angle) * arcRadius * 0.5 + amount * 1.5;
    const arcZ = (1 - Math.cos(angle)) * arcRadius * 0.3;
    
    // Combine with explode direction
    const dirOffset = explodeDir.clone().multiplyScalar(amount * 1.5);
    
    return basePos.clone()
      .add(dirOffset)
      .add(new THREE.Vector3(arcX, arcY, arcZ));
  };
  
  // Update explode positions with arc layout
  const updateExplodePositions = (amount, animated = false) => {
    const { gears, piston, loadedModel, modelParts, scene, connectorLines } = threeRef.current;
    const time = threeRef.current.floatTime;
    
    // Clear old connector lines
    connectorLines.forEach(line => scene?.remove(line));
    threeRef.current.connectorLines = [];
    
    // Update built-in gears
    const visibleGears = gears.filter(g => g?.visible);
    visibleGears.forEach((gear, i) => {
      if (!gear?.userData) return;
      
      const orig = gear.userData.originalPosition;
      const dir = gear.userData.explodeDirection || new THREE.Vector3(i === 0 ? -1 : i === 1 ? 1 : 0, i === 2 ? 1 : 0, 0);
      
      // Calculate arc position when amount > 0.5
      let targetPos;
      if (amount > 0.3) {
        targetPos = getArcPosition(i, visibleGears.length, amount, orig, dir);
      } else {
        targetPos = orig.clone().add(dir.clone().multiplyScalar(amount * 1.2));
      }
      
      // Smooth float animation when exploded
      if (amount > 0.2) {
        const phase = gear.userData.floatPhase || 0;
        const floatY = Math.sin(time * 1.2 + phase) * 0.08 * amount;
        const floatRot = Math.sin(time * 0.6 + phase) * 0.05 * amount;
        targetPos.y += floatY;
        gear.rotation.y = floatRot;
      }
      
      gear.position.copy(targetPos);
      
      // Update shadow
      gear.children.forEach(child => {
        if (child.userData?.isShadow) {
          child.material.opacity = 0.25 * (1 - amount * 0.6);
          child.position.y = -1.5 - gear.position.y + 0.01;
          child.scale.setScalar(1 + amount * 0.3);
        }
      });
      
      // Add connector lines
      if (showConnectors && amount > 0.2 && i > 0) {
        const prevGear = visibleGears[i - 1];
        if (prevGear) {
          const line = createConnectorLine(prevGear.position, gear.position);
          line.material.opacity = Math.min(amount * 0.8, 0.5);
          scene.add(line);
          threeRef.current.connectorLines.push(line);
        }
      }
    });
    
    // Update piston parts
    if (piston?.visible) {
      const pistonParts = [];
      piston.traverse(child => {
        if (child.isMesh && child.userData?.explodeDirection) {
          pistonParts.push(child);
        }
      });
      
      pistonParts.forEach((part, i) => {
        const orig = part.userData.originalPosition;
        const dir = part.userData.explodeDirection;
        
        let targetPos;
        if (amount > 0.3) {
          targetPos = getArcPosition(i, pistonParts.length, amount, orig, dir);
        } else {
          targetPos = orig.clone().add(dir.clone().multiplyScalar(amount));
        }
        
        // Float animation
        if (amount > 0.2) {
          const phase = part.userData.floatPhase || Math.random() * Math.PI * 2;
          part.userData.floatPhase = phase;
          targetPos.y += Math.sin(time * 1.5 + phase) * 0.06 * amount;
        }
        
        part.position.copy(targetPos);
        
        // Update shadow
        part.children.forEach(shadow => {
          if (shadow.userData?.isShadow) {
            shadow.material.opacity = 0.2 * (1 - amount * 0.5);
          }
        });
      });
    }
    
    // Update loaded model parts (car engine)
    if (loadedModel && modelParts.length > 0) {
      modelParts.forEach((part, i) => {
        if (!part.userData?.originalPosition) return;
        
        const orig = part.userData.originalPosition;
        const dir = part.userData.explodeDirection || new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          1,
          (Math.random() - 0.5) * 2
        ).normalize();
        
        let targetPos;
        if (amount > 0.3) {
          targetPos = getArcPosition(i, modelParts.length, amount * 1.0, orig, dir);
        } else {
          targetPos = orig.clone().add(dir.clone().multiplyScalar(amount * 2));
        }
        
        // Float animation
        if (amount > 0.2) {
          const phase = part.userData.floatPhase || 0;
          targetPos.y += Math.sin(time * 1.3 + phase) * 0.05 * amount;
          part.rotation.y = part.userData.originalRotation?.y || 0;
          part.rotation.y += Math.sin(time * 0.5 + phase) * 0.03 * amount;
        }
        
        part.position.copy(targetPos);
      });
    }
  };
  
  // Expose methods
  useImperativeHandle(ref, () => ({
    applyGestureControl: (action, delta) => {
      const { controls, camera } = threeRef.current;
      if (!controls || !camera) return false;
      
      try {
        if (action === 'rotate') {
          const spherical = new THREE.Spherical();
          const offset = camera.position.clone().sub(controls.target);
          spherical.setFromVector3(offset);
          spherical.theta -= delta.x;
          spherical.phi -= delta.y;
          spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
          offset.setFromSpherical(spherical);
          camera.position.copy(controls.target).add(offset);
          camera.lookAt(controls.target);
        } else if (action === 'zoom') {
          const direction = camera.position.clone().sub(controls.target);
          const currentDist = direction.length();
          const zoomFactor = Math.pow(2, -delta.zoom);
          const newDist = Math.max(controls.minDistance, Math.min(controls.maxDistance, currentDist * zoomFactor));
          direction.normalize().multiplyScalar(newDist);
          camera.position.copy(controls.target).add(direction);
          camera.lookAt(controls.target);
          controls.update();
        } else if (action === 'pan') {
          const right = new THREE.Vector3();
          const up = new THREE.Vector3(0, 1, 0);
          camera.getWorldDirection(right);
          right.cross(up).normalize();
          const panOffset = new THREE.Vector3();
          panOffset.addScaledVector(right, -delta.x);
          panOffset.addScaledVector(up, delta.y);
          camera.position.add(panOffset);
          controls.target.add(panOffset);
        }
        controls.update();
        return true;
      } catch (err) {
        return false;
      }
    },
    
    setExplodeAmount: (targetAmount, animate = true, duration = 2000) => {
      if (!animate) {
        threeRef.current.currentExplode = targetAmount;
        threeRef.current.targetExplode = targetAmount;
        updateExplodePositions(targetAmount);
      } else {
        threeRef.current.targetExplode = targetAmount;
        threeRef.current.explodeAnimating = true;
        threeRef.current.explodeStartTime = performance.now();
        threeRef.current.explodeStartValue = threeRef.current.currentExplode;
        threeRef.current.explodeDuration = duration;
      }
    },
    
    getExplodeAmount: () => threeRef.current.currentExplode,
    
    loadModel: async (url) => {
      const { scene, camera, controls } = threeRef.current;
      if (!scene) return;
      
      console.log('[Viewer3D] Loading model from:', url);
      
      // Remove existing loaded model
      if (threeRef.current.loadedModel) {
        scene.remove(threeRef.current.loadedModel);
        threeRef.current.loadedModel = null;
        threeRef.current.modelParts = [];
        if (threeRef.current.modelMixer) {
          threeRef.current.modelMixer = null;
        }
      }
      
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      loader.setDRACOLoader(dracoLoader);
      
      try {
        const gltf = await loader.loadAsync(url);
        const model = gltf.scene;
        
        console.log('[Viewer3D] Model loaded, processing...');
        
        // Calculate bounding box to center and scale
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        
        console.log('[Viewer3D] Model size:', size, 'Center:', center);
        
        // Scale to fit in view (max 5 units for better visibility)
        const maxDim = Math.max(size.x, size.y, size.z);
        const targetSize = 5;
        const scale = targetSize / maxDim;
        model.scale.setScalar(scale);
        
        // Center the model on the grid
        model.position.x = -center.x * scale;
        model.position.y = -center.y * scale + (size.y * scale) / 2;
        model.position.z = -center.z * scale;
        
        // Count vertices and meshes for performance info
        let totalVertices = 0;
        
        // First pass: collect all meshes (to avoid modifying while traversing)
        const meshes = [];
        model.traverse((child) => {
          if (child.isMesh && !child.userData?.isShadow) {
            meshes.push(child);
            if (child.geometry) {
              const posAttr = child.geometry.getAttribute('position');
              if (posAttr) totalVertices += posAttr.count;
            }
          }
        });
        
        console.log('[Viewer3D] Found', meshes.length, 'meshes,', totalVertices, 'vertices');
        
        // Second pass: process meshes for explode and add metadata
        const parts = [];
        meshes.forEach((child, partIndex) => {
          child.castShadow = true;
          child.receiveShadow = true;
          child.userData.selectable = true;
          child.userData.partName = child.name || `part_${partIndex}`;
          child.userData.originalPosition = child.position.clone();
          child.userData.originalRotation = child.rotation.clone();
          child.userData.originalScale = child.scale.clone();
          child.userData.partIndex = partIndex;
          child.userData.floatPhase = Math.random() * Math.PI * 2;
          
          // Calculate explode direction from model center (world space)
          const partWorldPos = new THREE.Vector3();
          child.getWorldPosition(partWorldPos);
          const modelCenter = new THREE.Vector3(model.position.x, model.position.y, model.position.z);
          const dir = partWorldPos.clone().sub(modelCenter).normalize();
          
          // Ensure we have a valid direction
          if (dir.length() < 0.1 || isNaN(dir.x)) {
            // Assign a random outward direction based on part index
            const angle = (partIndex / Math.max(1, meshes.length)) * Math.PI * 2;
            dir.set(Math.cos(angle), 0.5, Math.sin(angle)).normalize();
          }
          child.userData.explodeDirection = dir;
          
          // === CAD-STYLE MATERIAL SETUP (clean, professional) ===
          if (child.material) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((mat, matIndex) => {
              // Apply environment map from scene
              if (threeRef.current.envMap) {
                mat.envMap = threeRef.current.envMap;
              }
              
              // === CHECK FOR WHITE/DEFAULT MATERIALS AND APPLY CAD COLORS ===
              let isWhiteMaterial = false;
              if (mat.color) {
                const hsl = {};
                mat.color.getHSL(hsl);
                // Check if material is white/default (lightness > 0.9)
                isWhiteMaterial = hsl.l > 0.9 && hsl.s < 0.1;
              }
              
              // Apply alternating CAD colors to white materials
              if (isWhiteMaterial) {
                let cadColor, metalness, roughness;
                if (partIndex % 5 === 0) {
                  // Dark blue steel
                  cadColor = new THREE.Color(0x2a4a6a);
                  metalness = 0.75;
                  roughness = 0.4;
                } else if (partIndex % 3 === 0) {
                  // Bronze/copper
                  cadColor = new THREE.Color(0x8b4513);
                  metalness = 0.85;
                  roughness = 0.3;
                } else if (partIndex % 2 === 0) {
                  // Steel gray
                  cadColor = new THREE.Color(0x7a8a9a);
                  metalness = 0.8;
                  roughness = 0.35;
                } else {
                  // Medium steel
                  cadColor = new THREE.Color(0x6a7a8a);
                  metalness = 0.8;
                  roughness = 0.35;
                }
                
                // Create new metallic material for white parts
                const newMat = new THREE.MeshStandardMaterial({
                  color: cadColor,
                  metalness: metalness,
                  roughness: roughness,
                  envMapIntensity: 0.8
                });
                if (threeRef.current.envMap) {
                  newMat.envMap = threeRef.current.envMap;
                }
                child.material = Array.isArray(child.material) 
                  ? child.material.map((m, i) => i === matIndex ? newMat : m)
                  : newMat;
                return; // Skip further processing for this material
              }
              
              // === HIGH-CONTRAST CAD-STYLE MATERIAL SETUP (for non-white materials) ===
              if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
                // Moderate environment reflections for definition
                mat.envMapIntensity = 0.8;
                
                // AGGRESSIVELY brighten ALL materials for light background
                if (mat.color) {
                  const hsl = {};
                  mat.color.getHSL(hsl);
                  // Force minimum lightness of 0.5 for all materials
                  // Dark colors get boosted much more
                  const newLightness = hsl.l < 0.3 
                    ? Math.max(0.65, hsl.l * 3.0)  // Very dark -> bright
                    : hsl.l < 0.5 
                      ? Math.max(0.65, hsl.l * 2.2)  // Medium dark -> brighter
                      : Math.max(hsl.l, 0.65);      // Already light -> keep or boost
                  mat.color.setHSL(hsl.h, Math.min(hsl.s * 0.7, 0.5), newLightness);
                }
                
                // CAD-style metal: medium metalness, low roughness for highlights
                mat.metalness = 0.7;
                mat.roughness = 0.45;
                
                mat.needsUpdate = true;
              } else if (mat.isMeshBasicMaterial) {
                // Convert basic materials to standard with bright colors
                const color = mat.color ? mat.color.clone() : new THREE.Color(0xcccccc);
                const hsl = {};
                color.getHSL(hsl);
                const newLightness = Math.max(0.65, hsl.l * 2.5);
                color.setHSL(hsl.h, Math.min(hsl.s * 0.7, 0.4), newLightness);
                
                const newMat = new THREE.MeshStandardMaterial({
                  color: color,
                  map: mat.map,
                  metalness: 0.5,
                  roughness: 0.4,
                  envMapIntensity: 0.35
                });
                if (threeRef.current.envMap) {
                  newMat.envMap = threeRef.current.envMap;
                }
                child.material = newMat;
              } else if (mat.isMeshPhongMaterial || mat.isMeshLambertMaterial) {
                // Convert Phong/Lambert to Standard with bright colors
                const color = mat.color ? mat.color.clone() : new THREE.Color(0xcccccc);
                const hsl = {};
                color.getHSL(hsl);
                const newLightness = Math.max(0.65, hsl.l * 2.5);
                color.setHSL(hsl.h, Math.min(hsl.s * 0.7, 0.4), newLightness);
                
                const newMat = new THREE.MeshStandardMaterial({
                  color: color,
                  map: mat.map,
                  metalness: 0.5,
                  roughness: 0.4,
                  envMapIntensity: 0.35
                });
                if (threeRef.current.envMap) {
                  newMat.envMap = threeRef.current.envMap;
                }
                child.material = newMat;
              }
            });
          }
          
          parts.push(child);
        });
        
        // Skip individual shadow planes for performance on complex models
        // Instead, rely on the scene's shadow floor
        
        console.log('[Viewer3D] Processed', parts.length, 'parts');
        
        // Performance optimization: if high poly, enable frustum culling
        if (totalVertices > 100000) {
          console.log('[Viewer3D] High poly model detected, enabling optimizations');
          meshes.forEach((child) => {
            child.frustumCulled = true;
          });
        }
        
        threeRef.current.modelParts = parts;
        threeRef.current.loadedModel = model;
        scene.add(model);
        
        // Trigger visibility update by setting state
        setHasLoadedModel(true);
        
        // Fit camera to view the model with optimal framing
        const scaledSize = size.clone().multiplyScalar(scale);
        const maxScaledDim = Math.max(scaledSize.x, scaledSize.y, scaledSize.z);
        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = Math.abs(maxScaledDim / Math.sin(fov / 2)) * 1.1; // More padding
        
        // Position camera at 3/4 view angle (slightly above and to the front-right)
        const cameraAngle = Math.PI / 5; // 36 degrees
        camera.position.set(
          cameraDistance * Math.sin(cameraAngle) * 0.9,
          cameraDistance * 0.45, // Lower angle for better view
          cameraDistance * Math.cos(cameraAngle) * 0.9
        );
        controls.target.set(model.position.x, model.position.y, model.position.z);
        camera.lookAt(controls.target);
        controls.update();
        
        // Handle animations
        let hasAnimations = false;
        let animationNames = [];
        if (gltf.animations && gltf.animations.length > 0) {
          hasAnimations = true;
          animationNames = gltf.animations.map(clip => clip.name);
          const mixer = new THREE.AnimationMixer(model);
          gltf.animations.forEach((clip) => {
            const action = mixer.clipAction(clip);
            action.play();
          });
          mixer.timeScale = 0; // Paused by default
          threeRef.current.modelMixer = mixer;
          console.log('[Viewer3D] Animations found:', animationNames);
        }
        
        const result = {
          success: true,
          partCount: parts.length,
          vertexCount: totalVertices,
          hasAnimations,
          animationNames,
          partNames: parts.slice(0, 30).map(p => p.userData.partName), // Limit for UI
          boundingBox: { width: scaledSize.x, height: scaledSize.y, depth: scaledSize.z }
        };
        
        if (onModelLoaded) {
          onModelLoaded(result);
        }
        
        console.log('[Viewer3D] Model ready:', result);
        return result;
      } catch (err) {
        console.error('[Viewer3D] Failed to load model:', err);
        return { success: false, error: err.message };
      }
    },
    
    toggleAnimation: (play) => {
      const { modelMixer } = threeRef.current;
      if (modelMixer) {
        modelMixer.timeScale = play ? 1 : 0;
        setIsAnimationPlaying(play);
        if (onAnimationToggle) onAnimationToggle(play);
      }
    },
    
    updateLaserPointer: (fingerTip) => {
      const { scene, camera, renderer, laserLine, laserDot } = threeRef.current;
      if (!scene || !camera || !renderer) return null;
      
      if (laserLine) scene.remove(laserLine);
      if (laserDot) scene.remove(laserDot);
      
      if (!fingerTip) {
        threeRef.current.laserLine = null;
        threeRef.current.laserDot = null;
        return null;
      }
      
      const mouse = new THREE.Vector2((1 - fingerTip.x) * 2 - 1, -(fingerTip.y * 2 - 1));
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      
      const laserStart = raycaster.ray.origin.clone();
      const laserEnd = laserStart.clone().add(raycaster.ray.direction.clone().multiplyScalar(50));
      
      const geometry = new THREE.BufferGeometry().setFromPoints([laserStart, laserEnd]);
      const material = new THREE.LineBasicMaterial({ color: 0xff00ff, transparent: true, opacity: 0.7 });
      const line = new THREE.Line(geometry, material);
      scene.add(line);
      threeRef.current.laserLine = line;
      
      const intersects = raycaster.intersectObjects(scene.children, true);
      const hit = intersects.find(i => i.object.isMesh && !i.object.userData?.isShadow && i.object.userData?.selectable);
      
      if (hit) {
        const dotGeo = new THREE.SphereGeometry(0.08, 16, 16);
        const dotMat = new THREE.MeshBasicMaterial({ color: 0xff00ff, transparent: true, opacity: 0.9 });
        const dot = new THREE.Mesh(dotGeo, dotMat);
        dot.position.copy(hit.point);
        scene.add(dot);
        threeRef.current.laserDot = dot;
        return { object: hit.object, point: hit.point, partName: hit.object.userData?.partName };
      }
      return null;
    },
    
    highlightPart: (mesh) => {
      const { highlightedMesh, originalMaterials } = threeRef.current;
      if (highlightedMesh && highlightedMesh !== mesh) {
        const orig = originalMaterials.get(highlightedMesh);
        if (orig) highlightedMesh.material = orig;
      }
      if (!mesh) { threeRef.current.highlightedMesh = null; return; }
      if (!originalMaterials.has(mesh)) originalMaterials.set(mesh, mesh.material);
      const highlight = mesh.material.clone();
      highlight.emissive = new THREE.Color(0x00ffff);
      highlight.emissiveIntensity = 0.5;
      mesh.material = highlight;
      threeRef.current.highlightedMesh = mesh;
    },
    
    clearHighlight: () => {
      const { highlightedMesh, originalMaterials } = threeRef.current;
      if (highlightedMesh) {
        const orig = originalMaterials.get(highlightedMesh);
        if (orig) highlightedMesh.material = orig;
        threeRef.current.highlightedMesh = null;
      }
    },
    
    testRotate: (dir = 1) => {
      const { controls, camera } = threeRef.current;
      if (!controls || !camera) return;
      const spherical = new THREE.Spherical();
      spherical.setFromVector3(camera.position.clone().sub(controls.target));
      spherical.theta += dir * 0.5;
      camera.position.setFromSpherical(spherical).add(controls.target);
      camera.lookAt(controls.target);
      controls.update();
    },
    
    testZoom: (dir = 1) => {
      const { controls, camera } = threeRef.current;
      if (!controls || !camera) return;
      const d = camera.position.clone().sub(controls.target);
      const dist = d.length();
      const newDist = Math.max(controls.minDistance, Math.min(controls.maxDistance, dist * (1 - dir * 0.3)));
      d.normalize().multiplyScalar(newDist);
      camera.position.copy(controls.target).add(d);
      controls.update();
    },
    
    testPan: (dx = 1, dy = 0) => {
      const { controls, camera } = threeRef.current;
      if (!controls || !camera) return;
      const right = new THREE.Vector3();
      camera.getWorldDirection(right);
      right.cross(new THREE.Vector3(0, 1, 0)).normalize();
      camera.position.addScaledVector(right, dx);
      controls.target.addScaledVector(right, dx);
      controls.update();
    },
    
    resetCamera: () => {
      const { camera, controls } = threeRef.current;
      if (camera && controls) {
        camera.position.set(5, 4, 7);
        controls.target.set(0, 0, 0);
        camera.lookAt(0, 0, 0);
        controls.update();
      }
    },
    
    addMeasurementMarker: (point) => {
      const { scene, measurementMarkers } = threeRef.current;
      if (!scene) return;
      const geo = new THREE.SphereGeometry(0.05, 16, 16);
      const mat = new THREE.MeshBasicMaterial({ color: '#2ED573' });
      const marker = new THREE.Mesh(geo, mat);
      marker.name = 'measureMarker';
      marker.position.set(point.x, point.y, point.z);
      scene.add(marker);
      measurementMarkers.push(marker);
    },
    
    addMeasurementLine: (p1, p2) => {
      const { scene, measurementLines } = threeRef.current;
      if (!scene) return;
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(p1.x, p1.y, p1.z),
        new THREE.Vector3(p2.x, p2.y, p2.z)
      ]);
      const mat = new THREE.LineBasicMaterial({ color: '#F59E0B' });
      const line = new THREE.Line(geo, mat);
      scene.add(line);
      measurementLines.push(line);
    },
    
    clearMeasurements: () => {
      const { scene, measurementMarkers, measurementLines } = threeRef.current;
      if (!scene) return;
      measurementMarkers.forEach(m => scene.remove(m));
      measurementLines.forEach(l => scene.remove(l));
      threeRef.current.measurementMarkers = [];
      threeRef.current.measurementLines = [];
    },
    
    getPartsList: () => {
      const { gears, piston, modelParts } = threeRef.current;
      const parts = [];
      gears.forEach(g => { if (g?.visible && g.userData?.partName) parts.push(g.userData.partName); });
      if (piston?.visible) {
        piston.traverse(c => { if (c.isMesh && c.userData?.partName) parts.push(c.userData.partName); });
      }
      modelParts.forEach(p => { if (p.userData?.partName) parts.push(p.userData.partName); });
      return parts;
    },
    
    captureScreenshot: () => {
      const { renderer, scene, camera, composer } = threeRef.current;
      if (!renderer || !scene || !camera) return null;
      // Force render right before capture to ensure buffer is filled
      if (composer) {
        composer.render();
      } else {
        renderer.render(scene, camera);
      }
      return renderer.domElement.toDataURL('image/jpeg', 0.85);
    },
    
    // Section Plane Methods
    toggleSectionPlane: (enabled) => {
      const { scene, renderer, gears, piston, loadedModel, modelParts } = threeRef.current;
      if (!scene || !renderer) return;
      
      // Enable local clipping on renderer
      renderer.localClippingEnabled = enabled;
      
      if (enabled) {
        // Create plane if not exists
        if (!threeRef.current.sectionPlane) {
          threeRef.current.sectionPlane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
        }
        
        // Create helper plane mesh (visible cut indicator)
        if (!threeRef.current.sectionHelper) {
          const helperGeo = new THREE.PlaneGeometry(8, 8);
          const helperMat = new THREE.MeshBasicMaterial({
            color: 0x00d4ff,
            transparent: true,
            opacity: 0.03,
            side: THREE.DoubleSide,
            depthWrite: false
          });
          const helper = new THREE.Mesh(helperGeo, helperMat);
          helper.name = 'sectionHelper';
          
          // Add edge ring for visibility
          const edgeGeo = new THREE.RingGeometry(5.8, 6, 64);
          const edgeMat = new THREE.MeshBasicMaterial({
            color: 0x00d4ff,
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide
          });
          const edge = new THREE.Mesh(edgeGeo, edgeMat);
          helper.add(edge);
          
          threeRef.current.sectionHelper = helper;
        }
        scene.add(threeRef.current.sectionHelper);
        
        // Apply clipping to all meshes
        const plane = threeRef.current.sectionPlane;
        const applyClipping = (mesh) => {
          if (mesh.isMesh && mesh.material && mesh.name !== 'sectionHelper') {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach(mat => {
              mat.clippingPlanes = [plane];
              mat.clipShadows = true;
              mat.needsUpdate = true;
            });
          }
        };
        
        // Apply to all objects
        gears.forEach(g => g?.traverse(applyClipping));
        piston?.traverse(applyClipping);
        loadedModel?.traverse(applyClipping);
        modelParts.forEach(p => applyClipping(p));
        
        threeRef.current.sectionEnabled = true;
        
        // Update helper position
        const axis = threeRef.current.sectionAxis;
        const pos = threeRef.current.sectionPosition;
        if (threeRef.current.sectionHelper) {
          threeRef.current.sectionHelper.position.set(
            axis === 'x' ? pos : 0,
            axis === 'y' ? pos : 0,
            axis === 'z' ? pos : 0
          );
          threeRef.current.sectionHelper.rotation.set(
            axis === 'y' ? Math.PI / 2 : 0,
            axis === 'x' ? Math.PI / 2 : 0,
            0
          );
        }
      } else {
        // Remove clipping from all meshes
        const removeClipping = (mesh) => {
          if (mesh.isMesh && mesh.material) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach(mat => {
              mat.clippingPlanes = [];
              mat.needsUpdate = true;
            });
          }
        };
        
        gears.forEach(g => g?.traverse(removeClipping));
        piston?.traverse(removeClipping);
        loadedModel?.traverse(removeClipping);
        modelParts.forEach(p => removeClipping(p));
        
        // Remove helper
        if (threeRef.current.sectionHelper) {
          scene.remove(threeRef.current.sectionHelper);
        }
        
        threeRef.current.sectionEnabled = false;
      }
    },
    
    setSectionAxis: (axis) => {
      const plane = threeRef.current.sectionPlane;
      const helper = threeRef.current.sectionHelper;
      if (!plane) return;
      
      threeRef.current.sectionAxis = axis;
      const pos = threeRef.current.sectionPosition;
      
      // Update plane normal based on axis
      switch (axis) {
        case 'x':
          plane.normal.set(-1, 0, 0);
          if (helper) {
            helper.rotation.set(0, Math.PI / 2, 0);
            helper.position.set(pos, 0, 0);
          }
          break;
        case 'y':
          plane.normal.set(0, -1, 0);
          if (helper) {
            helper.rotation.set(Math.PI / 2, 0, 0);
            helper.position.set(0, pos, 0);
          }
          break;
        case 'z':
        default:
          plane.normal.set(0, 0, -1);
          if (helper) {
            helper.rotation.set(0, 0, 0);
            helper.position.set(0, 0, pos);
          }
          break;
      }
      plane.constant = pos;
    },
    
    setSectionPosition: (position) => {
      const plane = threeRef.current.sectionPlane;
      const helper = threeRef.current.sectionHelper;
      if (!plane) return;
      
      threeRef.current.sectionPosition = position;
      plane.constant = position;
      
      // Update helper position
      const axis = threeRef.current.sectionAxis;
      if (helper) {
        helper.position.set(
          axis === 'x' ? position : 0,
          axis === 'y' ? position : 0,
          axis === 'z' ? position : 0
        );
      }
    },
    
    getSectionState: () => ({
      enabled: threeRef.current.sectionEnabled,
      axis: threeRef.current.sectionAxis,
      position: threeRef.current.sectionPosition
    })
  }), [showConnectors, onModelLoaded, onAnimationToggle]);
  
  // Initialize
  useEffect(() => {
    if (initRef.current || !containerRef.current) return;
    initRef.current = true;
    
    const container = containerRef.current;
    const width = container.offsetWidth || 800;
    const height = container.offsetHeight || 600;
    
    const scene = new THREE.Scene();
    // LIGHT NEUTRAL GRAY BACKGROUND (like SolidWorks/Fusion 360)
    scene.background = new THREE.Color('#1a1a2e');
    // No fog for clean CAD look
    
    // Tighter FOV for better detail visibility (CAD standard ~45-50°)
    const camera = new THREE.PerspectiveCamera(48, width / height, 0.1, 1000);
    camera.position.set(7, 5, 9);
    
    // === SHARP CAD RENDERER SETUP ===
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // ACES tone mapping with higher exposure for visibility
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1; // Higher exposure for bright CAD look
    container.appendChild(renderer.domElement);
    
    // === PMREM GENERATOR FOR ENVIRONMENT ===
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    threeRef.current.pmremGenerator = pmremGenerator;
    
    // === SHARP STUDIO ENVIRONMENT (for crisp reflections) ===
    const createStudioEnvironment = () => {
      const envScene = new THREE.Scene();
      
      // Neutral gradient background
      const skyGeo = new THREE.SphereGeometry(50, 32, 32);
      const skyMat = new THREE.ShaderMaterial({
        side: THREE.BackSide,
        uniforms: {
          topColor: { value: new THREE.Color(0xfafafa) },
          bottomColor: { value: new THREE.Color(0xc8c8c8) },
          offset: { value: 10 },
          exponent: { value: 0.25 }
        },
        vertexShader: `
          varying vec3 vWorldPosition;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 topColor;
          uniform vec3 bottomColor;
          uniform float offset;
          uniform float exponent;
          varying vec3 vWorldPosition;
          void main() {
            float h = normalize(vWorldPosition + offset).y;
            gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
          }
        `
      });
      envScene.add(new THREE.Mesh(skyGeo, skyMat));
      
      // Sharp studio lights for defined highlights
      const addEnvLight = (color, intensity, x, y, z, size) => {
        const lightGeo = new THREE.SphereGeometry(size, 12, 12);
        const lightMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: intensity });
        const light = new THREE.Mesh(lightGeo, lightMat);
        light.position.set(x, y, z);
        envScene.add(light);
      };
      
      // Main overhead softbox (bright, defined)
      addEnvLight(0xffffff, 0.6, 5, 25, 8, 15);
      // Front key light reflection
      addEnvLight(0xffffff, 0.55, 8, 15, 15, 12);
      // Side fills (softer)
      addEnvLight(0xf8f8f8, 0.35, -15, 12, 0, 10);
      addEnvLight(0xf8f8f8, 0.35, 18, 10, -5, 10);
      // Rim highlight (subtle, for edge definition)
      addEnvLight(0xffffff, 0.3, 0, 8, -18, 8);
      
      return pmremGenerator.fromScene(envScene, 0.03).texture;
    };
    
    const envMap = createStudioEnvironment();
    scene.environment = envMap;
    threeRef.current.envMap = envMap;
    
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 1.5;
    controls.maxDistance = 40;
    controls.maxPolarAngle = Math.PI * 0.85;
    
    // === HIGH-CONTRAST SOLIDWORKS-STYLE LIGHTING ===
    // Higher ambient for visible details in shadows
    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);
    
    // Hemisphere light - strong sky/ground gradient for CAD feel
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xe0e0e0, 0.6);
    hemiLight.position.set(0, 20, 0);
    scene.add(hemiLight);
    
    // Key Light - VERY strong top-front light for crisp highlights
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.5);
    keyLight.position.set(8, 15, 12);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 2048;
    keyLight.shadow.mapSize.height = 2048;
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 50;
    keyLight.shadow.camera.left = -15;
    keyLight.shadow.camera.right = 15;
    keyLight.shadow.camera.top = 15;
    keyLight.shadow.camera.bottom = -15;
    keyLight.shadow.bias = -0.0001;
    keyLight.shadow.normalBias = 0.01;
    keyLight.shadow.radius = 3;
    scene.add(keyLight);
    threeRef.current.keyLight = keyLight;
    
    // Fill Light - Strong from left to lift shadows
    const fillLight = new THREE.DirectionalLight(0xffffff, 1.2);
    fillLight.position.set(-12, 10, 8);
    scene.add(fillLight);
    
    // Front Fill - Strong front illumination for visibility
    const frontFill = new THREE.DirectionalLight(0xffffff, 1.0);
    frontFill.position.set(0, 8, 18);
    scene.add(frontFill);
    
    // Side Fill - From right
    const sideFill = new THREE.DirectionalLight(0xffffff, 0.8);
    sideFill.position.set(15, 8, 0);
    scene.add(sideFill);
    
    // Back Fill - Moderate rear light for edge definition
    const backFill = new THREE.DirectionalLight(0xffffff, 0.6);
    backFill.position.set(0, 8, -15);
    scene.add(backFill);
    
    // Bottom Fill - Lift dark undersides
    const bottomFill = new THREE.DirectionalLight(0xffffff, 0.5);
    bottomFill.position.set(0, -5, 0);
    scene.add(bottomFill);
    
    // Rim Light - For edge definition
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
    rimLight.position.set(0, 5, -12);
    scene.add(rimLight);
    threeRef.current.rimLight = rimLight;
    
    // === POST-PROCESSING SETUP ===
    const composer = new EffectComposer(renderer);
    
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
    
    // Almost no bloom (CAD software doesn't have bloom)
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.05,  // Minimal bloom
      0.2,   // Tight radius
      0.98   // Very high threshold
    );
    composer.addPass(bloomPass);
    threeRef.current.bloomPass = bloomPass;
    
    // Anti-aliasing only
    const smaaPass = new SMAAPass(width * renderer.getPixelRatio(), height * renderer.getPixelRatio());
    composer.addPass(smaaPass);
    
    threeRef.current.composer = composer;
    
    // Subtle engineering grid (light gray lines on light background)
    const grid = new THREE.GridHelper(80, 80, 0x2a2a3e, 0x1e1e30);
    grid.position.y = -1.5;
    grid.material.opacity = 0.5;
    grid.material.transparent = true;
    scene.add(grid);
    
    // Fine grid for technical feel
    const fineGrid = new THREE.GridHelper(80, 400, 0x222235, 0x222235);
    fineGrid.position.y = -1.48;
    fineGrid.material.opacity = 0.25;
    fineGrid.material.transparent = true;
    scene.add(fineGrid);
    
    // Floor for very subtle contact shadows
    const floorGeo = new THREE.PlaneGeometry(80, 80);
    const floorMat = new THREE.ShadowMaterial({ opacity: 0.15 }); // Very subtle shadow
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.49;
    floor.receiveShadow = true;
    scene.add(floor);
    
    // Light floor surface (matches background)
    const surfaceFloorGeo = new THREE.PlaneGeometry(80, 80);
    const surfaceFloorMat = new THREE.MeshStandardMaterial({
      color: 0x151525,
      metalness: 0.0,
      roughness: 0.9,
      envMapIntensity: 0.05
    });
    const surfaceFloor = new THREE.Mesh(surfaceFloorGeo, surfaceFloorMat);
    surfaceFloor.rotation.x = -Math.PI / 2;
    surfaceFloor.position.y = -1.51;
    scene.add(surfaceFloor);
    
    // Create gears with CAD-style materials
    const gearGeometry = createGearGeometry();
    const gears = [];
    
    [
      { color: '#5599DD', pos: [0, 0, 0], scale: 1, explodeDir: [-1.5, 0.5, 0] },
      { color: '#4488CC', pos: [1.8, 0.2, 0], scale: 0.6, explodeDir: [1.5, 0.8, 0] },
      { color: '#44AA77', pos: [-0.5, 1.6, 0], scale: 0.4, explodeDir: [-0.5, 1.5, 0] }
    ].forEach((cfg, i) => {
      const mat = new THREE.MeshStandardMaterial({ 
        color: cfg.color, 
        metalness: 0.8, 
        roughness: 0.35,
        envMapIntensity: 0.25
      });
      const gear = new THREE.Mesh(gearGeometry, mat);
      gear.position.set(...cfg.pos);
      gear.scale.setScalar(cfg.scale);
      gear.rotation.x = -Math.PI / 2;
      gear.castShadow = true;
      gear.receiveShadow = true;
      gear.name = `gear_${['main', 'medium', 'small'][i]}`;
      gear.userData = {
        partName: gear.name, originalPosition: new THREE.Vector3(...cfg.pos),
        explodeDirection: new THREE.Vector3(...cfg.explodeDir),
        selectable: true, floatPhase: i * Math.PI * 0.7
      };
      const shadow = createShadowPlane(cfg.scale * 2);
      shadow.position.y = -1.5 / cfg.scale;
      shadow.scale.setScalar(1 / cfg.scale);
      gear.add(shadow);
      scene.add(gear);
      gears.push(gear);
    });
    
    const piston = createPistonGroup(false);
    piston.visible = false;
    scene.add(piston);
    
    threeRef.current = {
      ...threeRef.current,
      renderer, scene, camera, controls, gears, piston, grid,
      connectorLines: [], clock: new THREE.Clock()
    };
    
    // Click handler - uses refs to avoid re-creating effect when measureMode changes
    const handleClick = (e) => {
      if (!measureModeRef.current || !onMeasurementClickRef.current) return;
      const rect = renderer.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);
      const hit = intersects.find(i => i.object.isMesh && !i.object.userData?.isShadow);
      if (hit) onMeasurementClickRef.current({ x: hit.point.x, y: hit.point.y, z: hit.point.z });
    };
    renderer.domElement.addEventListener('click', handleClick);
    
    // Animation loop
    const animate = () => {
      threeRef.current.animationId = requestAnimationFrame(animate);
      const delta = threeRef.current.clock.getDelta();
      threeRef.current.floatTime = performance.now() * 0.001;
      
      // Handle explode animation
      if (threeRef.current.explodeAnimating) {
        const elapsed = performance.now() - threeRef.current.explodeStartTime;
        const progress = Math.min(elapsed / threeRef.current.explodeDuration, 1);
        const eased = easeOutBack(progress);
        
        const start = threeRef.current.explodeStartValue;
        const target = threeRef.current.targetExplode;
        threeRef.current.currentExplode = start + (target - start) * eased;
        
        updateExplodePositions(threeRef.current.currentExplode);
        
        if (progress >= 1) {
          threeRef.current.explodeAnimating = false;
        }
      } else if (threeRef.current.currentExplode > 0) {
        // Continue float animation even when not animating explode
        updateExplodePositions(threeRef.current.currentExplode);
      }
      
      // Gear rotation
      threeRef.current.gears.forEach((g, i) => {
        if (g?.visible && threeRef.current.currentExplode < 0.1) {
          g.rotation.z += 0.01 * (i % 2 === 0 ? 1 : -1);
        }
      });
      
      // Piston animation
      const p = threeRef.current.piston;
      if (p?.visible && threeRef.current.currentExplode < 0.1) {
        const t = threeRef.current.floatTime;
        const offset = Math.sin(t * 2) * 0.5;
        const head = p.getObjectByName('piston_head');
        const rod = p.getObjectByName('connecting_rod');
        if (head) head.position.y = head.userData.originalPosition.y + offset;
        if (rod) { rod.scale.y = 1 + offset * 0.3; rod.position.y = offset * 0.5; }
      }
      
      // Model animation mixer
      if (threeRef.current.modelMixer) {
        threeRef.current.modelMixer.update(delta);
      }
      
      controls.update();
      
      // Render with post-processing if available
      if (threeRef.current.composer) {
        threeRef.current.composer.render();
      } else {
        renderer.render(scene, camera);
      }
    };
    animate();
    
    // Resize handler - update composer size too
    const onResize = () => {
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      if (threeRef.current.composer) {
        threeRef.current.composer.setSize(w, h);
      }
    };
    window.addEventListener('resize', onResize);
    
    return () => {
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('click', handleClick);
      cancelAnimationFrame(threeRef.current.animationId);
      
      // Dispose post-processing
      if (threeRef.current.composer) {
        threeRef.current.composer.dispose();
      }
      if (threeRef.current.pmremGenerator) {
        threeRef.current.pmremGenerator.dispose();
      }
      if (threeRef.current.envMap) {
        threeRef.current.envMap.dispose();
      }
      
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initRef.current = false;
    };
  }, [showConnectors]); // Removed measureMode and onMeasurementClick - they use refs now
  
  // Update models visibility
  useEffect(() => {
    const { gears, piston, grid, loadedModel } = threeRef.current;
    if (!gears.length) return;
    
    if (grid) grid.visible = showGrid;
    
    const showGears = modelType === 'gear' || modelType === 'both';
    const showPiston = modelType === 'piston' || modelType === 'both';
    const showEngine = modelType === 'engine';
    
    console.log('[Viewer3D] Visibility update - modelType:', modelType, 'showEngine:', showEngine, 'hasLoadedModel:', hasLoadedModel);
    
    // Hide gears when showing engine
    gears.forEach((g, i) => {
      if (!g) return;
      const shouldShow = showGears && !showEngine;
      g.visible = shouldShow && (modelType !== 'both' || i === 0);
      if (g.material) g.material.wireframe = wireframe;
      if (g === gears[0]) g.userData.originalPosition.x = modelType === 'both' ? -1.5 : 0;
    });
    
    // Hide piston when showing engine
    if (piston) {
      piston.visible = showPiston && !showEngine;
      piston.position.x = modelType === 'both' ? 1.5 : 0;
      piston.traverse(c => { if (c.material) c.material.wireframe = wireframe; });
    }
    
    if (loadedModel) {
      loadedModel.visible = showEngine;
      loadedModel.traverse(c => { if (c.material) c.material.wireframe = wireframe; });
    }
    
    // Update explode
    if (!threeRef.current.explodeAnimating) {
      updateExplodePositions(explodeAmount);
      threeRef.current.currentExplode = explodeAmount;
      threeRef.current.targetExplode = explodeAmount;
    }
  }, [wireframe, showGrid, modelType, explodeAmount, hasLoadedModel]);
  
  // Handle render quality changes
  useEffect(() => {
    const { bloomPass, rimLight, keyLight, renderer } = threeRef.current;
    if (!bloomPass || !renderer) return;
    
    if (renderQuality === 'stylized') {
      // Stylized mode: more bloom, brighter rim light, higher exposure
      bloomPass.strength = 0.35;
      bloomPass.threshold = 0.8;
      bloomPass.enabled = true;
      if (rimLight) {
        rimLight.intensity = 0.8;
        rimLight.color.setHex(0xffffff);
      }
      if (keyLight) {
        keyLight.intensity = 2.0;
        keyLight.castShadow = true;
      }
      renderer.toneMappingExposure = 1.3;
    } else if (renderQuality === 'performance') {
      // Performance mode: no bloom, no shadows, higher exposure
      bloomPass.enabled = false;
      if (rimLight) rimLight.intensity = 0.3;
      if (keyLight) {
        keyLight.intensity = 2.2;
        keyLight.castShadow = false;
      }
      renderer.toneMappingExposure = 1.4;
    } else {
      // Clean/CAD mode: minimal bloom, strong even lighting, bright exposure
      bloomPass.strength = 0.02;
      bloomPass.threshold = 0.99;
      bloomPass.enabled = true;
      if (rimLight) {
        rimLight.intensity = 0.4;
        rimLight.color.setHex(0xffffff);
      }
      if (keyLight) {
        keyLight.intensity = 2.5;
        keyLight.castShadow = true;
      }
      renderer.toneMappingExposure = 1.4;
    }
  }, [renderQuality]);
  
  return (
    <div
      ref={containerRef}
      className={`w-full h-full ${measureMode ? 'cursor-crosshair' : ''}`}
      style={{ minHeight: '400px', background: '#1a1a2e' }}
      data-testid="viewer-3d-container"
    />
  );
});

Viewer3D.displayName = 'Viewer3D';
export default Viewer3D;
